''<template>
  <div
    id="top-header"
    :class="[
      'tw-relative tw-flex tw-flex-col tw-items-center tw-overflow-x-hidden',
      'tw-bg-ap-snow dark:tw-bg-ap-nero',
      'apTablet3:tw-h-[1800px] tw-h-[844px]',
      'tw-pt-[60px]'
    ]"
  >
    <section v-if="!isMobile" class="tw-flex tw-absolute tw-flex-col tw-max-w-[1920px] tw-w-full">
      <div
        id="vector-striped-circle"
        class="tw-absolute tw-max-h-[400px] tw-max-w-[400px] tw-z-[1] tw-left-[100px] tw-top-[140px]"
      >
        <img
          class="tw-w-full tw-h-full"
          :src="$cdn('images/home/img_vector_striped-circle-red.png')"
        >
      </div>
      <div
        id="vector-intro-card"
        class="tw-absolute tw-max-h-[926px] tw-max-w-[800px] tw-z-[2] tw-left-[320px] tw-top-[432px]"
      >
        <img class="tw-w-full tw-h-full" :src="$cdn('images/home/img_vector_intro-card.png')">
      </div>
      <div
        id="vector-intro-icon"
        class="tw-absolute tw-max-h-[1200px] tw-max-w-[1200px] tw-z-[1] tw-right-[80px] tw-top-[120px]"
      >
        <img
          class="tw-w-full tw-h-full"
          :src="$cdn(isDarkMode ? 'images/home/img_vector-intro-icon-dark.png' : 'images/home/img_vector-intro-icon-light.png')"
        >
      </div>
      <div
        id="text-intro"
        :class="[
          'tw-absolute tw-z-[20] tw-max-w-[900px] apDesktop2:tw-max-w-[1118px] tw-left-[480px] tw-top-[500px]',
          'apDesktop2:tw-text-h1-bold-desktop tw-text-h1-bold-mobile',
          'dark:tw-text-white tw-text-ap-nero'
        ]"
      >
        <h1>
          Next is an Intelligent Experience (IX) studio.
        </h1>
        <p class="tw-text-h3-semi-bold-desktop tw-font-[550] tw-pt-4">
          Transforming brands through a deep learning of the human psyche, from strategic comms to user experience at the heart of all that we do.
        </p>
      </div>
    </section>
    <section v-else class="tw-flex tw-relative tw-flex-col tw-w-full">
      <img
        class="tw-w-[400px] tw-h-[400px] tw-absolute apTablet:tw-right-[-125px] tw-right-[-250px] tw-top-[-80px]"
        :src="$cdn('images/home/img_vector_striped-circle-red.png')"
      >
      <div class="tw-flex tw-flex-col tw-gap-6 tw-px-6 apTablet:tw-px-[80px] apTablet:tw-pt-[71px] tw-pt-[64px]">
        <img class="tw-w-[77px] tw-h-[77px]" :src="$cdn('images/home/img_vector-intro-icon-light.png')">
        <div class="tw-text-h1-bold-mobile tw-max-w-[330px] apTablet:tw-max-w-[640px] dark:tw-text-white tw-text-ap-nero tw-relative tw-z-10">
          <h1>Next is an Intelligent Experience (IX) studio.</h1>
          <p class="tw-text-body-semi-bold-mobile tw-font-[550] tw-pt-4 tw-relative tw-z-10">
            Transforming brands by leveraging cutting-edge technology and design, with strategic comms and audience engagement at the heart of all we do.
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { watchDarkMode } from '../../support/helper';
gsap.registerPlugin(ScrollTrigger);

const isDarkMode = ref(false);
const isMobile = ref(window.innerWidth <= 1024);
const containerHeight = ref(window.innerWidth / 1.5);

let stopObserving;
let stopVisibilityObserver;

const refValue = ref('');

const emit = defineEmits(['sectionVisible']);

function initAnimation() {
  if (window.innerWidth < 1025) return;

  //vector striped circle
  const element1 = document.getElementById('vector-striped-circle');
  if (element1) {
    gsap.to(element1,
      {
        rotate: 45,
        translateY: -20,
        scrollTrigger: {
          trigger: element1,
          // markers: true,
          scrub: true,
          start: 'top center',
          end: 'bottom top'
        }
      }
    );
  }

  //vector intro card
  const element2 = document.getElementById('vector-intro-card');
  if (element2) {
    gsap.fromTo(element2,
      {
        y: 100,
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.7,
        ease: 'ease-in-out',
        scrollTrigger: {
          trigger: element2,
          // markers: true,
          start: 'top 45%',
          end: 'bottom top',
          toggleActions: 'play reverse play reverse',
        }
      }
    );
  }

  //vector intro icon
  const element3 = document.getElementById('vector-intro-icon');
  if (element3) {
    gsap.to(element3,
      {
        y: 500,
        ease: 'ease-in-out',
        scrollTrigger: {
          trigger: element3,
          scrub: true,
          // markers: true,
          start: 'top 10%',
          toggleActions: 'play reverse play reverse',
        }
      }
    );
  }

  //text intro
  const element4 = document.getElementById('text-intro');

  const refHeight = window.innerHeight;
  // const refWidth = window.innerWidth / 2;

  // // Coefficients from polynomial regression
  // const a = -3.751; // Linear term coefficient
  // const b = 0.005; // Quadratic term coefficient
  // const c = 683.316; // Intercept

  // // Function to calculate px based on the difference
  // function calculatePx() {
  //     const difference = Math.abs(refHeight - refWidth);
  //     const px = c + a * difference + b * Math.pow(difference, 2);
  //     return px;
  // }

  // refValue.value = calculatePx() + "px";

  if (element4 && refHeight < 900) {
    gsap.to(element4,
      {
        ease: 'ease-in-out',
        scrollTrigger: {
          trigger: element4,
          // markers: true,
          scrub: true,
          pin: true,
          start: 'top center',
          end: 'top top',
          // end: 'top top',
          toggleActions: 'play reverse play reverse',
        }
      }
    );
  }
}

function onResize() {
  isMobile.value = window.innerWidth <= 1280;
  const element1 = document.getElementById('vector-striped-circle');
  const element2 = document.getElementById('vector-intro-card');
  const element3 = document.getElementById('vector-intro-icon');
  const element4 = document.getElementById('text-intro');

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (!isMobile.value) {
    containerHeight.value = 1280;
  } else {
    containerHeight.value = 728;
  }
  const referenceWidth = 1920;
  const referenceHeight = 1280;

  function scaledSize(w: any) {
    return Math.min((viewportWidth / referenceWidth) * w, w);
  }

  function scaledPosition(pos: any) {
    if (viewportWidth > referenceWidth) {
      // If viewport is larger than referenceWidth, return the original position
      return pos;
    }
    // Otherwise, scale the position proportionally
    return (viewportWidth / referenceWidth) * pos;
  }

  //for top position, since the height will change based on viewport width / 1.5,
  // we need to calculate the top position based on the viewport height
  // if viewport height is 1280px, the top position will be 432px
  function scaledTop(pos: any) {
    // Otherwise, scale the position proportionally
    return (containerHeight.value / referenceHeight) * pos;
  }

  // Vector Striped Circle
  if (element1) {
    element1.style.width = `${scaledSize(400)}px`;
    element1.style.left = `${scaledPosition(100)}px`;
    element1.style.top = `${scaledTop(281)}px`;
  }

  // Vector Intro Card
  if (element2) {
    element2.style.width = `${scaledSize(800)}px`;
    element2.style.left = `${scaledPosition(320)}px`;
    // element2.style.top = `${scaledTop(561)}px`;
    element2.style.top = `${scaledTop(480)}px`;
  }

  // Vector Intro Icon
  if (element3) {
    element3.style.width = `${scaledSize(1200)}px`;
    element3.style.right = `${scaledPosition(40)}px`;
    element3.style.top = `${scaledTop(100)}px`;
  }

  // Text Intro
  if (element4) {
    element4.style.left = `${scaledPosition(482)}px`;
    if (viewportHeight < 900) {
      element4.style.top = `${scaledTop(viewportHeight * 0.5)}px`;
    } else {
      element4.style.top = `${scaledTop(820)}px`;
    }
  }
}

function monitorVisibility() {
  const section = document.getElementById('top-header');
  if (!section) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      emit('sectionVisible', entry.isIntersecting);
    },
    {
      root: null, // Observe relative to the viewport
      threshold: 0.2, // Trigger when at least 10% of the section is visible
    }
  );

  observer.observe(section);

  // Return a cleanup function
  return () => observer.disconnect();
}

onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize);
  window.addEventListener('load', initAnimation);
  stopObserving = watchDarkMode((value: any) => {
    isDarkMode.value = value;
  });
  stopVisibilityObserver = monitorVisibility();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  if (stopVisibilityObserver) stopVisibilityObserver();
  if (stopObserving) stopObserving();
});
</script>